import React, { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, InputField } from '@freelancelabs/components';

import { ContactsByEmailDocument } from '../../../graphql/__generated__/types';
import ContactMatchCard from './ContactMatchCard';
import { lookupRefusal } from './contactLookup';
import { Draft } from './draft';

interface Props {
    draft: Draft;
    onChange: (patch: Partial<Draft>) => void;
}

/**
 * Step 1: **the address, and the contact it designates** (PCG-84).
 *
 * The step no longer types an identity. First name, last name and function are gone:
 * what remains is an address and a « Rechercher » button, and `contactsByEmail`
 * brings the identity back from the **source**. `InviteUserInput` still carries the
 * three fields, so they are filled from the contact that was picked.
 *
 * **The strict option, and it is a decision rather than a default: only known
 * contacts are onboarded.** An address matching no ingested contact is refused with
 * its reason: a person Salesforce does not know has no account, no market and no
 * perimeter, so onboarding them produces a user who signs in and sees nothing.
 *
 * **Attention, the refusal is now held as well as shown** (PCG-85). Throughout PCG-84 it was
 * shown here and held nowhere: `inviteUser` did not read the contact table and took
 * no contact id, so it accepted an address the source did not know. The contract has
 * since changed: `contactId` is `ID!`, and the API confronts it with the address and
 * with the actor's portfolio. This screen remains the place the rule is *explained*,
 * and it is still not the place it is *enforced*: it says which gesture lifts each
 * refusal, which a status code cannot.
 *
 * ── Searching, and why it is a button
 *
 * The former step looked the address up as it was typed. This one waits for the
 * gesture: the query answers *is this person a known contact, and of which company*,
 * so firing it on every keystroke would ask it of every prefix of every address. The
 * button is also what the decision asks for.
 *
 * ── Picking, and why nothing is auto-selected
 *
 * **Attention, an address can designate several contacts**: `contact.email` carries no
 * uniqueness, deliberately, and a shared mailbox puts two different people behind one
 * address. The screen therefore makes the operator **choose**, and never elects.
 *
 * That holds for a single result too, and that is the deliberate part: the identity
 * is no longer typed, so adopting it is the one moment the operator gets to verify
 * the person they are about to grant an access to. An auto-selection would also fight
 * the « ce n'est pas le bon contact » reset, re-picking what was just dropped.
 */
const IdentityStep = ({ draft, onChange }: Props) => {
    /** The address the results on screen belong to. Empty until the first search. */
    const [searched, setSearched] = useState(draft.contact?.email ?? '');

    const typed = draft.email.trim();

    const { data, loading, error, refetch } = useQuery(ContactsByEmailDocument, {
        variables: { email: searched },
        skip: searched.length === 0,
    });

    /**
     * Whether the answer on screen is the answer for the address in the field.
     *
     * Apollo keeps the previous result while a new address is being typed, and the
     * results are what the operator picks an identity from: showing one address's
     * contacts under another's is how the wrong person gets adopted. When it goes
     * false the results are replaced by the invitation to search again, rather than
     * left standing.
     */
    const current = searched.length > 0 && searched === typed;

    const matches = data?.contactsByEmail;
    const contacts = current ? (matches?.contacts ?? []) : [];
    const refusal = current ? lookupRefusal(matches?.emptyReason) : null;
    const contactCount = contacts.length;

    const search = (event: React.FormEvent) => {
        event.preventDefault();
        if (!typed) {
            return;
        }
        // Re-running the same address is a legitimate gesture: the source may have
        // been ingested in between, and `skip`/`variables` alone would serve the
        // cached answer without a word.
        if (searched === typed) {
            void refetch();
            return;
        }
        setSearched(typed);
    };

    /**
     * Adopting a contact writes the identity **and** the address it was resolved for,
     * in one patch. `applyPatch` compares the contact's address against the draft's,
     * so the two have to travel together; setting them apart would make the identity
     * look stale the moment it was chosen.
     */
    const pick = (contact: (typeof contacts)[number]) =>
        onChange({
            contact: {
                id: contact.id,
                email: typed,
                firstName: contact.firstName,
                lastName: contact.lastName,
                jobFunction: contact.title,
                businessReference: contact.businessReference,
                onboardingStatus: contact.onboardingStatus,
            },
        });

    return (
        <div className="flex max-w-3xl flex-col gap-4">
            <div>
                <h2 className="text-sm font-semibold text-text-900">{c('Onboarding').t`Identify the contact`}</h2>
            </div>

            {/* A form, so Enter searches. A field with a button beside it that only
                answers to the mouse is a field people press Enter in and wait.

                Attention, le bouton porte `input-field-inline-action` : la colonne du champ
                réserve la hauteur de son message d'aide même vide, et un `items-end`
                alignerait donc le bouton sur le bas de cette réserve : une ligne plus
                bas que le champ auquel il répond. La classe du design system lui rend
                cette hauteur. */}
            <form className="flex flex-wrap items-end gap-3" onSubmit={search}>
                <InputField
                    className="min-w-64"
                    rootClassName="flex-1"
                    label={c('Onboarding field').t`Email address`}
                    assistiveText={c('Onboarding field')
                        .t`It carries the invitation and becomes the sign-in identifier. Case and surrounding spaces do not matter.`}
                    type="email"
                    value={draft.email}
                    onValue={(value: string) => onChange({ email: value })}
                    autoFocus
                />
                <Button
                    className="input-field-inline-action"
                    type="submit"
                    category="default"
                    loading={loading}
                    disabled={!typed}
                >
                    {c('Onboarding action').t`Search`}
                </Button>
            </form>

            {/* A query that fell over is not an address that matches nobody. Left to the
                refusal below, the operator reads « contact inconnu » and goes off to
                have a contact created that already exists. */}
            {error && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-danger-600" role="alert">
                    <span>
                        {c('Onboarding lookup')
                            .t`The search failed. Nothing can be concluded about this address — this is not an answer saying the contact is unknown.`}
                    </span>
                    <Button category="weak" size="small" onClick={() => refetch()}>
                        {c('Action').t`Retry`}
                    </Button>
                </div>
            )}

            {/* The address moved under a result that is still on screen. Saying so beats
                both alternatives: keeping the stale list invites picking the wrong
                person, and blanking it silently reads as a search that found nothing. */}
            {!error && !loading && searched.length > 0 && !current && (
                <p className="text-xs text-text-600" role="status">
                    {c('Onboarding lookup')
                        .t`The address has changed since the last search. Search again to see who it designates.`}
                </p>
            )}

            {/* The three empty answers, each with the gesture it calls for. The middle
                one deliberately says not to re-read the address: it is correct, and
                sending the operator back to it would be a loop. */}
            {!error && !loading && refusal && (
                <Alert type={refusal.tone === 'danger' ? 'danger' : 'warning'} title={refusal.title}>
                    <p className="mt-1 text-sm">{refusal.body}</p>
                </Alert>
            )}

            {/* Several people behind one address. Said before the list, because it
                changes what the list is: not a search result to confirm, but a choice
                to settle, and getting it wrong grants one person's perimeter to
                another. */}
            {contactCount > 1 && (
                <Alert type="warning" title={c('Onboarding lookup').t`This address designates several contacts.`}>
                    <p className="mt-1 text-sm">
                        {ngettext(
                            msgid`${contactCount} ingested contact carries this address — usually a shared mailbox, sometimes a duplicate at the source. Pick the person being onboarded: the invitation is sent under their identity.`,
                            `${contactCount} ingested contacts carry this address — usually a shared mailbox, sometimes duplicates at the source. Pick the person being onboarded: the invitation is sent under their identity.`,
                            contactCount
                        )}
                    </p>
                </Alert>
            )}

            {contactCount > 0 && (
                <ul className="flex flex-col gap-3">
                    {contacts.map((contact) => (
                        <ContactMatchCard
                            key={contact.id}
                            contact={contact}
                            selected={draft.contact?.id === contact.id}
                            amongSeveral={contactCount > 1}
                            onPick={() => pick(contact)}
                            onUnpick={() => onChange({ contact: null })}
                        />
                    ))}
                </ul>
            )}

            {/* Nothing searched yet. An empty area under a search field reads as a
                result, so it says which state it is in. */}
            {!error && !loading && searched.length === 0 && (
                <p className="rounded-lg border border-dashed border-border-norm px-4 py-6 text-center text-xs text-text-600">
                    {c('Onboarding lookup')
                        .t`Enter an address and search: only a contact known to the source can be onboarded, because the accounts, markets and perimeter all come from it.`}
                </p>
            )}
        </div>
    );
};

export default IdentityStep;
